import {
  Component,
  Expressions,
  Plugins,
  Types,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {Err, type Result} from '@packages/utilities/fp/result';
import {
  getName,
  getPluginName,
  namesEqual,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import type {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';

// Inlined from platform/elementTypeUtils to avoid dependency cycle:
const CHILDREN_PATH: ExpressionPath = [{in: 'Record' as const, at: 'children'}];

// Inlined from platform/ComponentElementUtils.getComponentRootElement to
// avoid dependency cycle (platform -> ... -> dom-validation -> ValidateNesting -> rich-text-validation):
export function getComponentRootElement(
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement
): ExpressionElement | null {
  const component = componentMap.get(componentType);
  return component
    ? Expressions.getElement(Component.getRender(component))
    : null;
}

/**
 * Returns whether an element or one of its descendants explicitly overrides a
 * SlotContent property. The presence of the property is enough: an empty
 * override still populates the instance Slot.
 */
export function containsRichTextSlotOverride(
  elements: readonly ExpressionElement[],
  componentMap: Component.ComponentMap
): boolean {
  const visitExpression = (expression: Expression): boolean => {
    const element = Expressions.getElement(expression);
    if (element) return visitElement(element);

    const typed = Expressions.getTyped(expression);
    if (typed) return visitExpression(typed.expr);

    const record = Expressions.getRecord(expression);
    if (record) {
      return Object.values(record).some(
        (value) => value != null && visitExpression(value)
      );
    }

    const list = Expressions.getList(expression);
    return list ? list.some(visitExpression) : false;
  };

  const visitElement = (element: ExpressionElement): boolean => {
    const component = componentMap.get(element.type);
    if (component) {
      const fields =
        Types.getRecordFields(Component.getDataType(component)) ?? {};
      const elementData = Expressions.getElementData(element);
      const typedData = Expressions.getTyped(elementData);
      const overrides =
        Expressions.getRecord(typedData?.expr ?? elementData) ?? {};
      if (
        Object.keys(overrides).some((fieldId) => {
          const field = fields[fieldId];
          return field != null && Types.isSlotContentType(field);
        })
      ) {
        return true;
      }
    }

    return visitExpression(Expressions.getElementData(element));
  };

  return elements.some(visitElement);
}

const dynamoWrapperType = qualify(
  Plugins.name('Dynamo'),
  Component.name('DynamoWrapper')
);

function isDynamoWrapper(element: ExpressionElement): boolean {
  return namesEqual(element.type, dynamoWrapperType);
}

/**
 * Checks if the element is a forbidden element type for Rich Text.
 * Forbidden types: Collection List (DynamoWrapper).
 */
export function isForbiddenRichTextElement(
  element: ExpressionElement
): boolean {
  return isDynamoWrapper(element);
}

function getChildren(
  element: ExpressionElement
): Array<ExpressionElement> | undefined {
  const data = Expressions.getElementData(element);
  const children = Expressions.getIn(data, CHILDREN_PATH);
  const childrenList = children ? Expressions.getList(children) : null;

  if (!childrenList) {
    return undefined;
  }

  return childrenList.reduce<Array<ExpressionElement>>((result, e) => {
    const el = Expressions.getElement(e);
    if (el) {
      result.push(el);
    }
    return result;
  }, []);
}

export type RichTextForbiddenElementType = 'DynamoWrapper';

const FORBIDDEN_ELEMENT_DISPLAY_NAMES: Record<
  RichTextForbiddenElementType,
  string
> = {
  DynamoWrapper: 'a collection list',
};

/**
 * Returns the user-friendly display name for a forbidden element type.
 */
export function getRichTextForbiddenElementDisplayName(
  elementType: RichTextForbiddenElementType
): string {
  return FORBIDDEN_ELEMENT_DISPLAY_NAMES[elementType];
}

export type RichTextNestingTranslationKey =
  | 'designer.components.component-ancestor-validation-failed'
  | 'designer.components.component-contains-forbidden-elements';

export type RichTextNestingTranslate = (
  key: RichTextNestingTranslationKey,
  options?: Record<string, string>
) => string;

/**
 * Builds the standard nesting validation error when a forbidden rich-text
 * element (or a component containing one) is placed inside rich text.
 */
export function buildForbiddenRichTextNestingError(params: {
  child: ExpressionElement;
  forbiddenElement: RichTextForbiddenElementType;
  ancestorLabel: string;
  translate: RichTextNestingTranslate;
}): Result<string, ExpressionElement> {
  const {child, forbiddenElement, ancestorLabel, translate} = params;

  if (isForbiddenRichTextElement(child)) {
    return Err(
      translate('designer.components.component-ancestor-validation-failed', {
        ancestor: ancestorLabel,
      })
    );
  }

  return Err(
    translate('designer.components.component-contains-forbidden-elements', {
      elementType: getRichTextForbiddenElementDisplayName(forbiddenElement),
    })
  );
}

/**
 * Omit instance to validate all defaults; pass it to exclude overridden fields,
 * including empty overrides, because their defaults will not render.
 */
export function findRichTextForbiddenElementInComponent(
  componentType: Plugins.QualifiedElement,
  componentMap: Component.ComponentMap,
  visited: Set<string> = new Set(),
  instance?: ExpressionElement
): RichTextForbiddenElementType | null {
  const component = componentMap.get(componentType);
  if (!component) return null;

  const rootElement = getComponentRootElement(componentMap, componentType);
  if (rootElement) {
    const forbidden = findRichTextForbiddenElement(
      rootElement,
      componentMap,
      visited
    );
    if (forbidden) return forbidden;
  }

  const defaultData = Component.getDefaultData(component);
  if (!defaultData) return null;

  const fields = Types.getRecordFields(Component.getDataType(component)) ?? {};
  let overrides: Record<string, Expression | undefined> = {};
  if (instance) {
    const data = Expressions.getElementData(instance);
    overrides =
      Expressions.getRecord(Expressions.getTyped(data)?.expr ?? data) ?? {};
  }

  for (const [fieldId, fieldType] of Object.entries(fields)) {
    if (!Types.isSlotContentType(fieldType) || overrides[fieldId] != null)
      continue;
    const defaultExpression = Expressions.getIn(defaultData, [
      {in: 'Function'},
      {in: 'Record', at: fieldId},
    ]);
    if (!defaultExpression) continue;
    const forbidden = visitExpression(defaultExpression, componentMap, visited);
    if (forbidden) return forbidden;
  }
  return null;
}

/**
 * Recursively finds forbidden elements in a component instance.
 * Checks for Collection Lists.
 * Returns the type of forbidden element found, or null if none.
 */
export function findRichTextForbiddenElement(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  visited: Set<string> = new Set()
): RichTextForbiddenElementType | null {
  // Prevent infinite loops with circular component references
  if (visited.has(element.id)) {
    return null;
  }
  visited.add(element.id);

  // Check if this element is a component instance (type exists in componentMap)
  const result = findRichTextForbiddenElementInComponent(
    element.type,
    componentMap,
    visited,
    element
  );
  if (result) return result;

  if (isDynamoWrapper(element)) return 'DynamoWrapper';

  return visitExpression(
    Expressions.getElementData(element),
    componentMap,
    visited
  );
}

function visitExpression(
  expression: Expression,
  componentMap: Component.ComponentMap,
  visited: Set<string>
): RichTextForbiddenElementType | null {
  const childElement = Expressions.getElement(expression);
  if (childElement) {
    return findRichTextForbiddenElement(childElement, componentMap, visited);
  }

  const typed = Expressions.getTyped(expression);
  if (typed) return visitExpression(typed.expr, componentMap, visited);

  const record = Expressions.getRecord(expression);
  if (record) {
    for (const value of Object.values(record)) {
      if (value != null) {
        const result = visitExpression(value, componentMap, visited);
        if (result) return result;
      }
    }
    return null;
  }

  const list = Expressions.getList(expression);
  if (list) {
    for (const value of list) {
      const result = visitExpression(value, componentMap, visited);
      if (result) return result;
    }
  }

  return null;
}

/**
 * Checks whether adding a component of `addedComponentType` inside a component
 * of `hostComponentType` would create a circular reference.
 *
 * A circular reference occurs when Component A contains (directly or
 * transitively) an instance of Component B, and Component B contains an
 * instance of Component A — or the simpler self-referencing case where
 * Component A is added inside its own render tree.
 *
 * This is needed because the Content Editor's component-add path bypasses
 * the staged component map cycle detection (`validateNoCircularComponents`).
 */
export function wouldCreateCircularReference(
  addedComponentType: Plugins.QualifiedElement,
  hostComponentType: Plugins.QualifiedElement,
  componentMap: Component.ComponentMap
): boolean {
  if (namesEqual(addedComponentType, hostComponentType)) {
    return true;
  }

  return componentContainsInstanceOf(
    addedComponentType,
    hostComponentType,
    componentMap,
    new Set()
  );
}

/**
 * Recursively checks if a component's render tree contains an instance
 * of `targetType`. Uses a set of visited component types to prevent
 * infinite recursion on pre-existing cycles in the component graph.
 */
function componentContainsInstanceOf(
  componentType: Plugins.QualifiedElement,
  targetType: Plugins.QualifiedElement,
  componentMap: Component.ComponentMap,
  visitedTypes: Set<string>
): boolean {
  const pluginName = Plugins.nameToString(getPluginName(componentType));
  const componentName = Component.nameToString(getName(componentType));
  const typeKey = `${pluginName}::${componentName}`;
  if (visitedTypes.has(typeKey)) {
    return false;
  }
  visitedTypes.add(typeKey);

  const rootEl = getComponentRootElement(componentMap, componentType);
  if (!rootEl) {
    return false;
  }

  return elementTreeContainsInstanceOf(
    rootEl,
    targetType,
    componentMap,
    visitedTypes
  );
}

function elementTreeContainsInstanceOf(
  element: ExpressionElement,
  targetType: Plugins.QualifiedElement,
  componentMap: Component.ComponentMap,
  visitedTypes: Set<string>
): boolean {
  if (namesEqual(element.type, targetType)) {
    return true;
  }

  // If this element is a component instance, check its render tree too
  if (componentMap.has(element.type)) {
    if (
      componentContainsInstanceOf(
        element.type,
        targetType,
        componentMap,
        visitedTypes
      )
    ) {
      return true;
    }
  }

  const children = getChildren(element);
  if (children) {
    for (const childEl of children) {
      if (
        elementTreeContainsInstanceOf(
          childEl,
          targetType,
          componentMap,
          visitedTypes
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

export type RichTextComponentValidationResult =
  | {
      type: 'forbidden-element';
      forbiddenElementType: RichTextForbiddenElementType;
    }
  | {type: 'inside-component-definition'}
  | {type: 'rt-property-override'}
  | {type: 'circular-reference'}
  | null;

/**
 * Validates whether a component can be added to a rich text field.
 * Returns null if valid, or a result describing the validation failure.
 */
export function validateComponentForRichText(params: {
  componentType: Plugins.QualifiedElement;
  componentMap: Component.ComponentMap;
  isRichTextSubtree: boolean;
  isUserComponentFocused: boolean;
  isFromModal: boolean;
  isEditingRichTextProp: boolean;
  hostComponentType: Plugins.QualifiedElement;
}): RichTextComponentValidationResult {
  const {
    componentType,
    componentMap,
    isRichTextSubtree,
    isUserComponentFocused,
    isFromModal,
    isEditingRichTextProp,
    hostComponentType,
  } = params;

  if (isRichTextSubtree) {
    const forbiddenElement = findRichTextForbiddenElementInComponent(
      componentType,
      componentMap
    );

    if (forbiddenElement) {
      return {
        type: 'forbidden-element',
        forbiddenElementType: forbiddenElement,
      };
    }
  }

  if (isRichTextSubtree && isUserComponentFocused) {
    return {type: 'inside-component-definition'};
  }

  if (isFromModal || isEditingRichTextProp) {
    return {type: 'rt-property-override'};
  }

  if (
    wouldCreateCircularReference(componentType, hostComponentType, componentMap)
  ) {
    return {type: 'circular-reference'};
  }

  return null;
}
